import React, { createContext, useCallback, useContext, useState } from "react";

import _ from "lodash";

import { useAppState } from "../../../../../context/StateContext";
import { Despatch } from "../../../../../models/despatch";
import { Location } from "../../../../../models/location";
import { AddressTypeKeys, DespatchesDetailsFields } from "../../../../../util/enums";
import { DespatchChangeKeys, DespatchChangeOptions, DespatchObjects } from "../Maintenance/types";

interface DespatchContextProps {
  addressToUpdate: AddressToUpdate | undefined;
  despatch?: Despatch;
  hasUnsavedChanges: boolean;
  changedOptions: DespatchChangeKeys;
  setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  handleChangeOptions: (newOption: DespatchChangeOptions, noChanges?: boolean) => void;
  setDespatch: React.Dispatch<React.SetStateAction<Despatch>>;
  handleUpdateDespatch: (attribute: keyof Despatch | "all", value: any) => void;
  handleAddAddress: (type: AddressTypeKeys, value: Location) => void;
  handleUpdateAddress: (type: AddressTypeKeys, value: Location, index: number) => void;
  handleDeleteAddress: (type: AddressTypeKeys, index: number) => void;
  handleAddAddressList: (type: AddressTypeKeys, value: Location[]) => void;
  startEditingAddress: (type: AddressTypeKeys, index: number) => void;
  stopEditingAddress: () => void;
}

const DespatchContext = createContext<DespatchContextProps>({} as DespatchContextProps);

interface DespatchProviderProps {
  children: React.ReactChild;
}

export interface AddressToUpdate {
  index: number;
  type: AddressTypeKeys;
  address: Location;
}

const DespatchProvider = ({ children }: DespatchProviderProps) => {
  const { hasUnsavedChanges, setHasUnsavedChanges } = useAppState();

  const [despatch, setDespatch] = useState<Despatch>({} as Despatch);
  const [addressToUpdate, setAddressToUpdate] = useState<AddressToUpdate>();
  const [changedOptions, setChangedOptions] = useState<DespatchChangeKeys>({
    [DespatchObjects.DESPATCH]: false,
    [DespatchObjects.PRODUCTS]: false,
    [DespatchObjects.SHIPPING_ITEMS]: false,
    [DespatchObjects.TRUCK]: false,
  });

  const handleChangeOptions = useCallback(
    (newOption: DespatchChangeOptions, noChanges?: boolean) => {
      if (!noChanges) setHasUnsavedChanges(true);

      if (!changedOptions[newOption])
        setChangedOptions((prev) => {
          return {
            ...prev,
            [newOption]: true,
          };
        });
    },
    [changedOptions]
  );

  const handleUpdateDespatch = useCallback((attribute: keyof Despatch | "all", value: any) => {
    const despatchDetails = Object.values(DespatchesDetailsFields) as string[];

    if (despatchDetails.includes(attribute)) handleChangeOptions(DespatchObjects.DESPATCH);
    if (attribute === "products") handleChangeOptions(DespatchObjects.PRODUCTS);
    if (attribute === "shippingItems") handleChangeOptions(DespatchObjects.SHIPPING_ITEMS);
    if (attribute === "fleets") handleChangeOptions(DespatchObjects.TRUCK);

    if (attribute && attribute !== "all") {
      if (!["string", "number", "boolean", "object"].includes(typeof value))
        setDespatch((state: Despatch) => ({ ...state, [attribute]: [...value] }));
      else setDespatch((state: Despatch) => ({ ...state, [attribute]: value }));
    } else {
      setDespatch({ ...value });
    }
  }, []);

  const handleAddAddress = (type: AddressTypeKeys, value: Location) => {
    if (despatch?.[type]?.length) handleUpdateDespatch(type, [...despatch[type]!, value]);
    else handleUpdateDespatch(type, [value]);
  };

  const handleAddAddressList = (type: AddressTypeKeys, value: Location[]) => {
    if (despatch?.[type]?.length) handleUpdateDespatch(type, [...despatch[type]!, ...value]);
    else handleUpdateDespatch(type, [...value]);
  };

  const handleUpdateAddress = (type: AddressTypeKeys, value: Location, index: number) => {
    const cloned = _.cloneDeep(despatch?.[type]);

    if (cloned?.length) {
      cloned[index] = value;
      handleUpdateDespatch(type, cloned);
    }
  };

  const handleDeleteAddress = (type: AddressTypeKeys, index: number) => {
    const cloned = _.cloneDeep(despatch?.[type]);

    if (cloned?.length) {
      cloned?.splice(index, 1);
      handleUpdateDespatch(type, cloned);
    }
  };

  const startEditingAddress = (type: AddressTypeKeys, index: number) => {
    const cloned = _.cloneDeep(despatch?.[type]);

    if (cloned?.length) {
      setAddressToUpdate({ type, index, address: cloned?.[index] });
    }
  };

  const stopEditingAddress = () => {
    setAddressToUpdate(undefined);
  };

  return (
    <DespatchContext.Provider
      value={{
        addressToUpdate,
        changedOptions,
        despatch,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        setDespatch,
        handleAddAddress,
        handleAddAddressList,
        handleChangeOptions,
        handleDeleteAddress,
        handleUpdateDespatch,
        handleUpdateAddress,
        stopEditingAddress,
        startEditingAddress,
      }}
    >
      {children}
    </DespatchContext.Provider>
  );
};

const useDespatchContext = () => {
  const context = useContext(DespatchContext);

  return context;
};

export { DespatchContext, DespatchProvider, useDespatchContext };
